import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useCreatePath, usePermissions, useRedirect } from "react-admin";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActionArea,
  Link as MuiLink,
  Stack,
  Typography,
  CircularProgress,
  Divider,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { Link } from "react-router-dom";
import { fromGregorian } from "@/utils/hijri-date-utils";
import { formatListDate, formatWeekdayFullUtc, parseDayjs } from "@/utils/date-format";
import { formatRazaEventStartTimeLabel } from "@/utils/raza-event-time";
import {
  isOhbatRazaEvent,
  razaEventKindLabel,
  razaEventPurposeLabel,
  razaEventTitle,
} from "@/utils/raza-event-display";
import { formatRazaEventSadaratListLabel } from "@/utils/format-raza-event-sadarat-display";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { callApi } from "@/dataprovider/misc-apis";
import { hasAnyPermission, hasPermission } from "@/utils/permission-utils";
import { getChartColorSequence } from "@/theme/chartPalette";

function fmt(n: unknown): string {
  if (n === null || n === undefined) return "0";
  return Number(n).toLocaleString();
}

const formatMajlisDateUtc = (date: unknown) =>
  date ? formatListDate(String(date), { utc: true }) : "—";

const formatMajlisDayOfWeekUtc = (date: unknown) =>
  date ? formatWeekdayFullUtc(String(date)) : "—";

const formatMajlisHijriUtc = (date: unknown) =>
  date ? fromGregorian(parseDayjs(String(date), true).toDate(), "code") : "—";

type StatCardProps = {
  title: string;
  value: string;
  subtitle?: string;
  color?: string;
  onNavigate?: () => void;
};

const statCardContentSx = {
  p: { xs: 1.25, sm: 2 },
  "&:last-child": { pb: { xs: 1.25, sm: 2 } },
};

function StatCard({ title, value, subtitle, color = "primary.main", onNavigate }: StatCardProps) {
  const body = (
    <>
      <Typography
        variant="subtitle2"
        color="text.secondary"
        gutterBottom
        sx={{ typography: { xs: "caption", sm: "subtitle2" }, lineHeight: 1.35 }}
      >
        {title}
      </Typography>
      <Typography variant="h5" sx={{ color, typography: { xs: "h6", sm: "h5" } }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.5, fontSize: { xs: "0.65rem", sm: undefined } }}
        >
          {subtitle}
        </Typography>
      )}
    </>
  );

  if (onNavigate) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardActionArea onClick={onNavigate} sx={{ height: "100%", textAlign: "left" }}>
          <CardContent sx={statCardContentSx}>{body}</CardContent>
        </CardActionArea>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={statCardContentSx}>{body}</CardContent>
    </Card>
  );
}

function typeDisplayName(type: string): string {
  const m: Record<string, string> = {
    Jaman: "Jaman",
    Food_packets: "Food packets",
    Salawaat: "Salawaat",
    "Raza-backed": "Raza-backed",
  };
  return m[type] || type;
}

type AttendanceBySectorRow = { sector: string; count: number };
type MajlisByTypeRow = { type: string; count: number };

type OhbatDashboardStats = {
  asOf?: string;
  majlis?: {
    completed?: number;
    upcoming?: number;
    upcomingOhbat?: number;
    total?: number;
  };
  attendance?: {
    totalRecords?: number;
    distinctAttendees?: number;
    avgPerCompletedMajlis?: number;
  };
  attendanceBySector?: AttendanceBySectorRow[];
  majlisCompletedByType?: MajlisByTypeRow[];
  makhsoosMarksCount?: number;
  sadaratsCount?: number;
};

function isOhbatDashboardStats(data: unknown): data is OhbatDashboardStats {
  return typeof data === "object" && data !== null;
}

type NextEventRow = {
  id?: string;
  date?: unknown;
  ohbatType?: string | null;
  title?: string | null;
  purposeName?: string | null;
  startTime?: string | null;
  hostName?: string | null;
  hostItsNo?: string | null;
  mobileNo?: string | null;
  hostSector?: string | null;
  hostSubSector?: string | null;
  razaRequest?: { purpose?: string } | null;
  sadarat?: { name?: string } | null;
  khidmatguzar?: { Full_Name?: string } | null;
  khidmatguzarItsNo?: string | null;
  zakereen?: { Full_Name?: string } | null;
  zakereenItsNo?: string | null;
};

function isNextEventRow(row: unknown): row is NextEventRow {
  return typeof row === "object" && row !== null && "id" in row && (row as NextEventRow).id != null;
}

export default function RazaEventSubtypeOverview() {
  const theme = useTheme();
  const chartColors = useMemo(() => getChartColorSequence(theme), [theme]);
  const redirect = useRedirect();
  const createPath = useCreatePath();
  const { permissions } = usePermissions();
  const [stats, setStats] = useState<OhbatDashboardStats | null>(null);
  const [nextEvent, setNextEvent] = useState<NextEventRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [upcomingError, setUpcomingError] = useState<string | null>(null);

  const can = (p: string) => hasPermission(permissions, p);
  const canAny = (list: string[]) => hasAnyPermission(permissions, list);
  const canLoadStats = canAny(["razaEvents.view", "razaEventAttendance.view"]);
  const attendanceCreateBase = createPath({ resource: "razaEventAttendances", type: "create" });
  const toAttendanceCreate = (majlisId: string) =>
    `${attendanceCreateBase}?razaEventId=${encodeURIComponent(majlisId)}`;

  const loadDashboard = useCallback(async () => {
    if (!canLoadStats) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    setUpcomingError(null);
    try {
      const [statsResult, upcomingResult] = await Promise.allSettled([
        callApi({
          location: "raza-events",
          method: "GET",
          id: "dashboard-stats",
        }),
        callApi({
          location: "raza-events",
          method: "GET",
          id: "next-upcoming",
        }),
      ]);

      if (statsResult.status === "fulfilled") {
        const raw = statsResult.value?.data;
        setStats(isOhbatDashboardStats(raw) ? raw : null);
      } else {
        setStats(null);
        setError(
          statsResult.reason instanceof Error
            ? statsResult.reason.message
            : "Failed to load dashboard stats"
        );
      }

      if (upcomingResult.status === "fulfilled") {
        const body = upcomingResult.value?.data as { rows?: unknown[] } | undefined;
        const first = body?.rows?.[0];
        setNextEvent(isNextEventRow(first) ? first : null);
      } else {
        setNextEvent(null);
        setUpcomingError(
          upcomingResult.reason instanceof Error
            ? upcomingResult.reason.message
            : "Failed to load upcoming events"
        );
      }
    } finally {
      setLoading(false);
    }
  }, [canLoadStats]);

  useEffect(() => {
    void loadDashboard();
  }, [loadDashboard]);

  const sectorChartData = useMemo(() => {
    const rows = stats?.attendanceBySector || [];
    if (rows.length === 0) return [];
    const mapped = rows.map((r) => ({
      name: r.sector.length > 22 ? `${r.sector.slice(0, 20)}…` : r.sector,
      fullName: r.sector,
      count: r.count,
    }));
    if (mapped.length <= 12) return mapped;
    const top = mapped.slice(0, 11);
    const otherCount = mapped.slice(11).reduce((s, r) => s + r.count, 0);
    return [...top, { name: "Other", fullName: "Other sectors", count: otherCount }];
  }, [stats]);

  const typePieData = useMemo(
    () =>
      (stats?.majlisCompletedByType || []).map((r) => ({
        name: typeDisplayName(r.type),
        value: r.count,
      })),
    [stats]
  );

  const linkCards: { title: string; description: string; to: string }[] = [];
  if (can("razaEvents.view")) {
    linkCards.push({
      title: "Raza events",
      description: "Calendar, create and edit events",
      to: "/raza-events",
    });
  }
  if (canAny(["razaEvents.view", "razaEventAttendance.view"])) {
    linkCards.push({
      title: "Ohbat Attendance",
      description: "Upcoming and past ohbat majlis",
      to: "/razaEventsUpcoming",
    });
  }
  if (can("sadarats.view")) {
    linkCards.push({
      title: "Sadarats",
      description: "Sadarat master",
      to: "/sadarats",
    });
  }
  if (can("makhsoosItsData.view")) {
    linkCards.push({
      title: "Makhsoos ITS",
      description: "Marks for sector matching",
      to: "/makhsoosItsData",
    });
  }
  if (can("razaEventAttendance.view")) {
    linkCards.push({
      title: "Attendance log",
      description: "All recorded attendance",
      to: "/razaEventAttendances",
    });
  }

  const nextEventMeta = nextEvent
    ? [
        razaEventPurposeLabel(nextEvent) &&
        razaEventPurposeLabel(nextEvent) !== razaEventTitle(nextEvent)
          ? razaEventPurposeLabel(nextEvent)
          : null,
        (() => {
          const label = formatRazaEventSadaratListLabel(nextEvent, { empty: "" });
          return label ? `Sadarat: ${label}` : null;
        })(),
        nextEvent.khidmatguzar?.Full_Name || nextEvent.khidmatguzarItsNo
          ? `Khidmat: ${nextEvent.khidmatguzar?.Full_Name || String(nextEvent.khidmatguzarItsNo)}`
          : null,
        nextEvent.zakereen?.Full_Name || nextEvent.zakereenItsNo
          ? `Zakereen: ${nextEvent.zakereen?.Full_Name || String(nextEvent.zakereenItsNo)}`
          : null,
        nextEvent.mobileNo ? `Contact: ${String(nextEvent.mobileNo)}` : null,
        [nextEvent.hostSector, nextEvent.hostSubSector].filter(Boolean).join(" · ") || null,
        nextEvent.date ? `Day: ${formatMajlisDayOfWeekUtc(nextEvent.date)}` : null,
        nextEvent.date ? `Hijri: ${formatMajlisHijriUtc(nextEvent.date)}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  const asOfLabel =
    stats?.asOf != null && stats.asOf !== ""
      ? `Figures as of start of day (UTC): ${formatListDate(String(stats.asOf), { utc: true })}`
      : null;

  return (
    <>
      <Box
        sx={{
          mt: 1,
          mb: 2,
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6">Raza events</Typography>
          {asOfLabel && (
            <Typography variant="caption" color="text.secondary" display="block">
              {asOfLabel}
            </Typography>
          )}
        </Box>
        {can("razaEvents.create") && (
          <Button
            variant="contained"
            component={Link}
            to={createPath({ resource: "raza-events", type: "create" })}
          >
            Schedule event
          </Button>
        )}
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
        Quick links
      </Typography>
      <Box
        component="nav"
        aria-label="Quick links"
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          columnGap: 0.75,
          rowGap: 0.25,
          mb: 2,
        }}
      >
        {linkCards.map((c, i) => (
          <Fragment key={c.to}>
            {i > 0 ? (
              <Typography
                component="span"
                variant="caption"
                color="text.disabled"
                aria-hidden
                sx={{ userSelect: "none" }}
              >
                ·
              </Typography>
            ) : null}
            <MuiLink
              component={Link}
              to={c.to}
              variant="body2"
              underline="hover"
              title={c.description}
              sx={{ fontWeight: 500, lineHeight: 1.4 }}
            >
              {c.title}
            </MuiLink>
          </Fragment>
        ))}
      </Box>

      <Divider sx={{ my: 2 }} />

      {canLoadStats && (
        <>
          {loading && (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          )}
          {!loading && error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          {!loading && upcomingError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {upcomingError}
            </Typography>
          )}
          {!loading && !upcomingError && (
            <Card variant="outlined" sx={{ mb: 3, borderColor: "primary.main", borderWidth: 1 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Next upcoming event
                </Typography>
                {nextEvent?.id ? (
                  <>
                    <Typography variant="h6" sx={{ wordBreak: "break-word" }}>
                      {razaEventTitle(nextEvent)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {formatMajlisDateUtc(nextEvent.date)} · {razaEventKindLabel(nextEvent)} ·{" "}
                      {formatRazaEventStartTimeLabel(nextEvent.startTime)}
                    </Typography>
                    {nextEventMeta ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {nextEventMeta}
                      </Typography>
                    ) : null}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
                      {can("razaEventAttendance.create") && isOhbatRazaEvent(nextEvent) && (
                        <Button
                          variant="contained"
                          component={Link}
                          to={toAttendanceCreate(String(nextEvent.id))}
                        >
                          Record attendance
                        </Button>
                      )}
                      {can("razaEvents.view") && (
                        <Button
                          variant="outlined"
                          onClick={() => redirect("show", "raza-events", nextEvent.id)}
                        >
                          View event
                        </Button>
                      )}
                      {can("razaEvents.view") && (
                        <Button variant="text" component={Link} to="/raza-events">
                          All events
                        </Button>
                      )}
                    </Stack>
                  </>
                ) : (
                  <Typography color="text.secondary">No upcoming events scheduled.</Typography>
                )}
              </CardContent>
            </Card>
          )}

          {!loading && stats && (
            <>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid
                  size={{
                    xs: 6,
                    sm: 6,
                    md: 3,
                  }}
                >
                  <StatCard
                    title="Completed events (past)"
                    value={fmt(stats.majlis?.completed)}
                    subtitle="Before today (UTC)"
                    color="success.main"
                    {...(can("razaEvents.view")
                      ? { onNavigate: () => redirect("/raza-events") }
                      : {})}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 6,
                    md: 3,
                  }}
                >
                  <StatCard
                    title="Upcoming events"
                    value={fmt(stats.majlis?.upcoming)}
                    subtitle="Today and later (all types)"
                    color="info.main"
                    {...(can("razaEvents.view")
                      ? { onNavigate: () => redirect("/raza-events") }
                      : {})}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 6,
                    md: 3,
                  }}
                >
                  <StatCard
                    title="Ohbat Attendance"
                    value={fmt(stats.majlis?.upcomingOhbat ?? stats.majlis?.upcoming)}
                    subtitle="Upcoming ohbat majlis"
                    color="primary.main"
                    {...(canAny(["razaEvents.view", "razaEventAttendance.view"])
                      ? { onNavigate: () => redirect("/razaEventsUpcoming") }
                      : {})}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 6,
                    md: 3,
                  }}
                >
                  <StatCard
                    title="Attendance records"
                    value={fmt(stats.attendance?.totalRecords)}
                    subtitle={`${fmt(stats.attendance?.distinctAttendees)} unique ITS`}
                    color="secondary.main"
                    {...(can("razaEventAttendance.view")
                      ? { onNavigate: () => redirect("/razaEventAttendances") }
                      : {})}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 6,
                    md: 3,
                  }}
                >
                  <StatCard
                    title="Avg records / completed ohbat"
                    value={fmt(stats.attendance?.avgPerCompletedMajlis)}
                    subtitle="Ohbat majlis attendance ratio"
                    color="text.primary"
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 6,
                    md: 3,
                  }}
                >
                  <StatCard
                    title="Makhsoos marks"
                    value={fmt(stats.makhsoosMarksCount)}
                    subtitle="ITS marked makhsoos"
                    {...(can("makhsoosItsData.view")
                      ? { onNavigate: () => redirect("/makhsoosItsData") }
                      : {})}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 6,
                    md: 3,
                  }}
                >
                  <StatCard
                    title="Sadarats"
                    value={fmt(stats.sadaratsCount)}
                    subtitle="Active records"
                    {...(can("sadarats.view") ? { onNavigate: () => redirect("/sadarats") } : {})}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 6,
                    sm: 6,
                    md: 3,
                  }}
                >
                  <StatCard
                    title="Total events"
                    value={fmt(stats.majlis?.total)}
                    subtitle="All scheduled"
                    {...(can("razaEvents.view")
                      ? { onNavigate: () => redirect("/raza-events") }
                      : {})}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid
                  size={{
                    xs: 12,
                    md: 7,
                  }}
                >
                  <Card sx={{ p: 2, height: 380 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Attendance by sector (ITS)
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ mb: 1 }}
                    >
                      From itsdata match on attendee ITS; unknown if not in itsdata
                    </Typography>
                    {sectorChartData.length === 0 ? (
                      <Typography color="text.secondary" sx={{ py: 4 }} align="center">
                        No attendance yet
                      </Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={sectorChartData}
                          margin={{ top: 8, right: 16, left: 0, bottom: 64 }}
                        >
                          <XAxis
                            dataKey="name"
                            interval={0}
                            angle={-28}
                            textAnchor="end"
                            height={70}
                            tick={{ fontSize: 11 }}
                          />
                          <YAxis allowDecimals={false} />
                          <Tooltip
                            formatter={(value) => [fmt(value), "Attendance"]}
                            labelFormatter={(_, payload) => {
                              const row = (
                                payload as unknown as { payload?: { fullName?: string } }[]
                              )?.[0]?.payload;
                              return String(row?.fullName ?? "");
                            }}
                          />
                          <Bar
                            dataKey="count"
                            fill={theme.palette.primary.main}
                            name="Count"
                            radius={[4, 4, 0, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                  </Card>
                </Grid>
                <Grid
                  size={{
                    xs: 12,
                    md: 5,
                  }}
                >
                  <Card sx={{ p: 2, height: 380 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      Completed events by type
                    </Typography>
                    {typePieData.length === 0 ? (
                      <Typography color="text.secondary" sx={{ py: 4 }} align="center">
                        No completed events yet
                      </Typography>
                    ) : (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={typePieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={false}
                          >
                            {typePieData.map((entry, index) => (
                              <Cell
                                key={entry.name}
                                fill={chartColors[index % chartColors.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value) => fmt(value)} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </Card>
                </Grid>
              </Grid>
            </>
          )}
        </>
      )}
    </>
  );
}
